import { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useLanguage } from '@/context/LanguageContext';
import { useAuth } from '@/context/AuthContext';
import { Calendar } from '@/components/ui/calendar';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { CheckCircle2, CalendarDays, Clock, Car, Plus, Minus, ChevronLeft, ChevronRight, ChevronDown, ChevronUp, Fuel, Upload, Loader2, Camera, Image, Eye, X, Receipt, Trash2, Lock, MapPin, AlertTriangle, Banknote, Award, TrendingUp, Wallet } from 'lucide-react';
import SalaryCard from '@/components/SalaryCard';
import { queueCheckin } from '@/lib/offlineCheckin';
import { fr, ru, ro, uk } from 'date-fns/locale';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OVERTIME_OPTIONS = [0, 30, 60, 90, 120, 150, 180, 210, 240];

// Helper function for translations (fr, ru, md, uk)
const getText = (language, fr, ru, md, uk) => {
  if (language === 'ru') return ru;
  if (language === 'md') return md;
  if (language === 'uk') return uk;
  return fr;
};

// Get local date string in YYYY-MM-DD format (avoids UTC timezone shift from toISOString)
// Regroupement des chantiers dans les selecteurs de pointage (ordre Pavel
// 29/09/2026 : « il faut qu'on puisse choisir aussi les chantiers en Entretien »).
// 0 = chantiers en cours (in_progress, en_attente), 1 = contrats d'entretien
// (status maintenance / entretien, ceux de la page Entretien), 2 = le reste
// (stand-by, archives). Chaque groupe porte son titre dans la liste.
const projectGroupRank = (p) => {
  const st = p?.status;
  if (st === 'in_progress' || st === 'en_attente') return 0;
  if (st === 'maintenance' || st === 'entretien') return 1;
  return 2;
};
const PROJECT_GROUP_LABELS = [
  { fr: 'Chantiers en cours', ru: 'Текущие объекты', md: 'Șantiere în curs', uk: 'Поточні об\'єкти' },
  { fr: 'Entretien', ru: 'Обслуживание', md: 'Întreținere', uk: 'Обслуговування' },
  { fr: 'Autres chantiers', ru: 'Другие объекты', md: 'Alte șantiere', uk: 'Інші об\'єкти' },
];
const groupProjects = (projects, language) => {
  const groups = PROJECT_GROUP_LABELS.map((l, i) => ({ key: i, label: l[language] || l.fr, items: [] }));
  (projects || []).forEach(p => { groups[projectGroupRank(p)].items.push(p); });
  return groups.filter(g => g.items.length > 0);
};

const getLocalDateStr = (date) => {
  const d = date || new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

import { markModuleSeen } from '@/lib/moduleBadges';

export default function AttendancePage() {
  // Ouvrir le module efface sa pastille de notifications
  useEffect(() => { markModuleSeen(['attendance']); }, []);
  const { t, language } = useLanguage();
  const { user, isPreviewMode } = useAuth();
  const isManager = user?.role === 'manager';
  const userCompany = user?.company || 'La Piscine Container';
  const isEBC = userCompany === 'Eco Bati Construction';
  
  const [attendance, setAttendance] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checkingIn, setCheckingIn] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const pointerAutoRef = useRef(false);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [showOvertimeDialog, setShowOvertimeDialog] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [overtimeValue, setOvertimeValue] = useState(0);
  const [hasTravelValue, setHasTravelValue] = useState(false);
  const [isHalfDayValue, setIsHalfDayValue] = useState(false);
  
  // EBC: Project selection for attendance
  const [projects, setProjects] = useState([]);
  // Chargement de la liste des chantiers : si l'appel echoue (reseau mobile
  // capricieux sur chantier), la liste restait vide SANS message ni relance, et
  // la modale bloquante "chantier manquant pour hier" devenait une impasse
  // (incident Vlad, 14/08/2026). On expose l'etat pour pouvoir reessayer.
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [selectedProjectIds, setSelectedProjectIds] = useState([]);
  
  // Chantier demande AU MOMENT du pointage (Pavel 11/09/2026). Avant, le
  // pointage partait sans chantier et la question n'arrivait que le lendemain,
  // par la modale bloquante : 28 a 38 % des journees finissaient sans chantier.
  const [showCheckinProjectDialog, setShowCheckinProjectDialog] = useState(false);
  const [checkinProjectId, setCheckinProjectId] = useState('');
  // Plusieurs chantiers dans la journée (Pavel 17/09/2026) : liste des chantiers cochés.
  const [checkinProjectIds, setCheckinProjectIds] = useState([]);

  // Missing project yesterday (blocking dialog)
  const [missingProjectRecord, setMissingProjectRecord] = useState(null);
  const [showMissingProjectDialog, setShowMissingProjectDialog] = useState(false);
  const [missingProjectId, setMissingProjectId] = useState('');
  
  // Month filter state (synced with calendar navigation)
  const [calendarMonth, setCalendarMonth] = useState(new Date());
  const filterMonth = calendarMonth.getMonth();
  const filterYear = calendarMonth.getFullYear();
  
  // Fuel states
  const [fuelStats, setFuelStats] = useState(null);
  const [uploadingFuel, setUploadingFuel] = useState(false);
  const [showFuelDialog, setShowFuelDialog] = useState(false);
  const [showFuelPhoto, setShowFuelPhoto] = useState(null);
  const [showWebcam, setShowWebcam] = useState(false);
  const [webcamStream, setWebcamStream] = useState(null);
  const fuelGalleryRef = useRef(null);
  const fuelCameraRef = useRef(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  
  // Note de frais states
  const [expenseStats, setExpenseStats] = useState(null);
  const [showExpenseDialog, setShowExpenseDialog] = useState(false);
  const [expenseTitle, setExpenseTitle] = useState('');
  const [expensePhoto, setExpensePhoto] = useState(null);
  const [expenseAmount, setExpenseAmount] = useState('');
  const [uploadingExpense, setUploadingExpense] = useState(false);
  const [showExpensePhoto, setShowExpensePhoto] = useState(null);
  const expenseGalleryRef = useRef(null);
  const expenseCameraRef = useRef(null);
  
  // Detect mobile device (Android or iOS)
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

  // History toggle (collapsed by default)
  const [historyOpen, setHistoryOpen] = useState(false);
  
  // Detailed salary data for the breakdown
  const [salaryDetail, setSalaryDetail] = useState(null);

  const fetchAttendance = async () => {
    try {
      const response = await axios.get(`${API}/attendance/me`);
      setAttendance(response.data);
    } catch (error) {
      console.error('Failed to fetch attendance:', error);
    } finally {
      setLoading(false);
    }
  };
  
  // Fetch EBC projects for attendance
  const fetchProjects = async () => {
    setProjectsLoading(true);
    try {
      // Liste ALLEGEE (quelques Ko) : la liste complete depasse 20 Mo a cause des
      // photos d'etapes en base64, elle expirait sur un telephone en 4G et le
      // selecteur restait vide (incidents Vlad 14/08/2026, Vlad et Livu 11/09/2026).
      const response = await axios.get(`${API}/projects/light`, { timeout: 20000 });
      // Tous les projets sauf les stocks permanents (archives compris),
      // chantiers en cours d'abord pour que le bon choix soit en haut de liste.
      const allProjects = (Array.isArray(response.data) ? response.data : [])
        .filter(p => !p.is_stock)
        .sort((a, b) => {
          const rank = (p) => projectGroupRank(p);
          return rank(a) - rank(b) || (a.name || '').localeCompare(b.name || '');
        });
      setProjects(allProjects);
    } catch (error) {
      console.error('Failed to fetch projects:', error);
    } finally {
      setProjectsLoading(false);
    }
  };

  useEffect(() => {
    fetchAttendance();
    fetchFuelStats();
    fetchExpenseStats();
    fetchSalaryDetail();
    checkBirthdays();
    fetchProjects();
    checkMissingProject();
    // Trigger maintenance reminders check
    axios.post(`${API}/equipment/check-maintenance-reminders`).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  // Check for birthdays on app load
  const checkBirthdays = async () => {
    try {
      await axios.post(`${API}/birthdays/check-and-notify`);
    } catch (error) {
      console.error('Birthday check failed:', error);
    }
  };

  // La modale bloquante ne doit JAMAIS s'ouvrir sur une liste vide : si les
  // chantiers manquent (appel echoue au chargement), on relance la requete a
  // l'ouverture (incident Vlad, 14/08/2026).
  useEffect(() => {
    if (showMissingProjectDialog && projects.length === 0 && !projectsLoading) {
      fetchProjects();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showMissingProjectDialog]);

  // Check if yesterday's attendance is missing a project
  const checkMissingProject = async () => {
    try {
      const response = await axios.get(`${API}/attendance/missing-project`);
      if (response.data.missing && response.data.record) {
        setMissingProjectRecord(response.data.record);
        setShowMissingProjectDialog(true);
      }
    } catch (error) {
      console.error('Missing project check failed:', error);
    }
  };

  // Fetch detailed salary breakdown for the selected month
  const fetchSalaryDetail = async () => {
    try {
      const m = filterMonth + 1;
      const y = filterYear;
      const res = await axios.get(`${API}/salary/my-monthly`, { params: { month: m, year: y } });
      setSalaryDetail(res.data);
    } catch (e) {
      console.error('Salary detail fetch error:', e);
    }
  };

  // Save project for yesterday's missing record
  const saveMissingProject = async () => {
    if (!missingProjectRecord || !missingProjectId) return;
    try {
      await axios.put(`${API}/attendance/${missingProjectRecord.id}`, { project_id: missingProjectId }, { timeout: 20000 });
      setShowMissingProjectDialog(false);
      setMissingProjectRecord(null);
      setMissingProjectId('');
      fetchAttendance();
      toast.success(getText(language, 
        'Chantier enregistré pour hier !', 
        'Объект сохранён за вчера!', 
        'Șantier salvat pentru ieri!', 
        'Об\'єкт збережено за вчора!'
      ));
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (detail) {
        toast.error(detail);
      } else {
        toast.error(getText(language, 
          'Erreur lors de la sauvegarde. Réessayez.', 
          'Ошибка сохранения. Повторите.', 
          'Eroare la salvare. Reîncercați.', 
          'Помилка збереження. Повторіть.'
        ));
      }
    }
  };
  
  useEffect(() => {
    fetchFuelStats();
    fetchExpenseStats();
    fetchSalaryDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterMonth, filterYear]);
  
  const fetchFuelStats = async () => {
    try {
      const response = await axios.get(`${API}/fuel/monthly`, {
        params: { month: filterMonth + 1, year: filterYear }
      });
      setFuelStats(response.data);
    } catch (error) {
      console.error('Failed to fetch fuel stats:', error);
    }
  };
  
  const fetchExpenseStats = async () => {
    try {
      const response = await axios.get(`${API}/expenses/monthly`, {
        params: { month: filterMonth + 1, year: filterYear }
      });
      setExpenseStats(response.data);
    } catch (error) {
      console.error('Failed to fetch expense stats:', error);
    }
  };
  
  // Preparation de la photo avant envoi.
  //
  // Historique, mesure dans la base : jusqu'au 26/08 les tickets partaient bruts
  // (3 a 5 Mo en base64) et passaient. La compression ajoutee le 27/08 a fait
  // tomber le taux de reussite a ZERO : plus aucun ticket enregistre depuis, ni
  // pour Marius (roue infinie) ni pour Vlad ("photo non lisible"). Sur iPhone,
  // FileReader ou le decodage <img> d'un HEIC 48 Mpx peut ne declencher ni
  // onload ni onerror, et canvas.toDataURL rend "data:," des que iOS depasse
  // son budget memoire (d'autant plus vite que la page tenait deja 21 Mo de
  // photos d'historique, voir le chargement paresseux ci-dessous).
  //
  // Regle appliquee ici : LA COMPRESSION NE DOIT JAMAIS BLOQUER UN ENVOI. Elle
  // reste une optimisation. Toute panne retombe sur l'envoi de la photo telle
  // quelle, ce qui est exactement ce qui fonctionnait avant le 27/08.
  const readFileAsDataURL = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = () => reject(new Error('lecture_impossible'));
      reader.onload = () => (reader.result ? resolve(reader.result) : reject(new Error('lecture_vide')));
      reader.readAsDataURL(file);
    });

  const withTimeout = (promise, ms, label) =>
    Promise.race([
      promise,
      new Promise((_, reject) => setTimeout(() => reject(new Error(label)), ms)),
    ]);

  // Remonte au serveur la raison exacte d'un repli, pour diagnostiquer sans
  // avoir le telephone de l'employe sous la main. Silencieux et non bloquant.
  const reportPhotoIssue = (reason, file) => {
    try {
      axios.post(`${API}/client-log`, {
        scope: 'photo_upload',
        reason: String(reason || 'inconnu'),
        file_type: file?.type || '',
        file_size: file?.size || 0,
        user_agent: navigator.userAgent,
      }, { timeout: 8000 }).catch(() => {});
    } catch (_) {}
  };

  // Decodage : createImageBitmap d'abord (decodeur natif, hors thread principal,
  // le plus sobre en memoire sur iOS), repli sur <img> + objectURL.
  const decodeImageFile = async (file) => {
    if (typeof createImageBitmap === 'function') {
      try {
        const bitmap = await withTimeout(createImageBitmap(file), 15000, 'bitmap_timeout');
        if (bitmap && bitmap.width) return { source: bitmap, url: null };
      } catch (_) { /* on tente le repli */ }
    }
    const url = URL.createObjectURL(file);
    const img = await withTimeout(
      new Promise((resolve, reject) => {
        const el = new Image();
        el.onload = () => resolve(el);
        el.onerror = () => reject(new Error('decodage_impossible'));
        el.src = url;
      }),
      15000,
      'decodage_timeout'
    ).catch((e) => {
      try { URL.revokeObjectURL(url); } catch (_) {}
      throw e;
    });
    return { source: img, url };
  };

  const compressPhotoFile = async (file, maxWidth = 1600, quality = 0.85) => {
    // Photo deja legere : rien a gagner, on evite le canvas et son risque.
    if (file?.size && file.size < 1.2 * 1024 * 1024) {
      try {
        return await withTimeout(readFileAsDataURL(file), 20000, 'lecture_timeout');
      } catch (_) { /* on continue par le chemin normal */ }
    }

    let reason = 'inconnu';
    let decoded = null;
    try {
      decoded = await decodeImageFile(file);
      const { source } = decoded;
      const sw = source.width;
      const sh = source.height;
      if (!sw || !sh) throw new Error('image_vide');

      // Plusieurs tailles decroissantes : si iOS refuse le canvas faute de
      // memoire, on retente plus petit avant d'abandonner la compression.
      for (const wanted of [maxWidth, 1200, 900, 700]) {
        const target = Math.min(wanted, sw);
        const height = Math.round((sh * target) / sw);
        let canvas = null;
        try {
          canvas = document.createElement('canvas');
          canvas.width = target;
          canvas.height = height;
          canvas.getContext('2d').drawImage(source, 0, 0, target, height);
          const out = canvas.toDataURL('image/jpeg', quality);
          // iOS rend "data:," quand le canvas a depasse sa limite memoire.
          if (out && out.length > 1000) return out;
          reason = `canvas_vide_${target}`;
        } catch (e) {
          reason = `canvas_erreur_${target}:${e.message}`;
        } finally {
          // Libere immediatement la memoire graphique sur iOS.
          if (canvas) { canvas.width = 0; canvas.height = 0; }
        }
      }
    } catch (e) {
      reason = e?.message || 'echec_decodage';
    } finally {
      if (decoded) {
        if (decoded.url) { try { URL.revokeObjectURL(decoded.url); } catch (_) {} }
        if (decoded.source?.close) { try { decoded.source.close(); } catch (_) {} }
      }
    }

    // Repli ultime : la photo part telle quelle. Plus lourde, mais elle PART.
    reportPhotoIssue(reason, file);
    return await withTimeout(readFileAsDataURL(file), 30000, 'lecture_timeout');
  };

  // Chargement paresseux des justificatifs : les listes mensuelles ne
  // transportent plus les photos, on ne telecharge que celle qu'on ouvre.
  useEffect(() => {
    if (!showFuelPhoto || showFuelPhoto.photo_data || !showFuelPhoto.has_photo) return;
    const id = showFuelPhoto.id;
    let cancelled = false;
    axios.get(`${API}/fuel/${id}/photo`)
      .then((r) => {
        if (cancelled) return;
        setShowFuelPhoto((prev) => (prev && prev.id === id ? { ...prev, photo_data: r.data.photo_data } : prev));
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [showFuelPhoto]);

  useEffect(() => {
    if (!showExpensePhoto || showExpensePhoto.photo_data || !showExpensePhoto.has_photo) return;
    const id = showExpensePhoto.id;
    let cancelled = false;
    axios.get(`${API}/expenses/${id}/photo`)
      .then((r) => {
        if (cancelled) return;
        setShowExpensePhoto((prev) => (prev && prev.id === id ? { ...prev, photo_data: r.data.photo_data } : prev));
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [showExpensePhoto]);

  const handleExpensePhotoSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error({ fr: 'Veuillez sélectionner une image', ru: 'Выберите изображение', md: 'Selectați o imagine', uk: 'Виберіть зображення' }[language] || 'Veuillez sélectionner une image');
      return;
    }

    try {
      const compressed = await compressPhotoFile(file);
      setExpensePhoto(compressed);
    } catch (err) {
      toast.error({ fr: "Photo illisible, reprenez-la avec l'appareil photo", ru: 'Фото не читается, сделайте новый снимок камерой', md: 'Fotografie ilizibilă, refaceți-o cu camera', uk: 'Фото не читається, зробіть новий знімок камерою' }[language] || "Photo illisible, reprenez-la avec l'appareil photo");
    }
  };
  
  const handleExpenseSubmit = async () => {
    if (!expenseTitle.trim()) {
      toast.error({ fr: 'Le titre est obligatoire', ru: 'Заголовок обязателен', md: 'Titlul este obligatoriu', uk: 'Заголовок обов\'язковий' }[language] || 'Le titre est obligatoire');
      return;
    }
    // La photo n'est plus obligatoire (demande Pavel, 31/08/2026) : un justificatif
    // perdu ou illisible ne doit pas empecher d'enregistrer la depense.
    if (!expenseAmount || parseFloat(expenseAmount) <= 0) {
      toast.error({ fr: 'Le montant est obligatoire', ru: 'Сумма обязательна', md: 'Suma este obligatorie', uk: 'Сума обов\'язкова' }[language] || 'Le montant est obligatoire');
      return;
    }
    
    setUploadingExpense(true);
    
    try {
      await axios.post(`${API}/expenses/upload`, {
        title: expenseTitle,
        photo_data: expensePhoto,
        total_amount: parseFloat(expenseAmount),
        date: getLocalDateStr()
      });
      
      await fetchExpenseStats();
      
      toast.success({ fr: 'Note de frais ajoutée', ru: 'Расход добавлен', md: 'Cheltuială adăugată', uk: 'Витрату додано' }[language] || 'Note de frais ajoutée');
      setShowExpenseDialog(false);
      resetExpenseForm();
    } catch (error) {
      toast.error({ fr: 'Erreur lors de l\'ajout', ru: 'Ошибка добавления', md: 'Eroare la adăugare', uk: 'Помилка додавання' }[language] || 'Erreur lors de l\'ajout');
    } finally {
      setUploadingExpense(false);
    }
  };
  
  const resetExpenseForm = () => {
    setExpenseTitle('');
    setExpensePhoto(null);
    setExpenseAmount('');
  };
  
  const handleDeleteExpense = async (expenseId) => {
    const confirmMsg = { 
      fr: 'Voulez-vous vraiment supprimer cette note de frais ?', 
      ru: 'Вы уверены, что хотите удалить этот расход?', 
      md: 'Sigur doriți să ștergeți această cheltuială?',
      uk: 'Ви впевнені, що хочете видалити цю витрату?'
    }[language] || 'Voulez-vous vraiment supprimer cette note de frais ?';
    
    if (!window.confirm(confirmMsg)) return;
    
    try {
      await axios.delete(`${API}/expenses/${expenseId}`);
      await fetchExpenseStats();
      toast.success({ fr: 'Dépense supprimée', ru: 'Расход удалён', md: 'Cheltuială ștearsă', uk: 'Витрату видалено' }[language] || 'Dépense supprimée');
    } catch (error) {
      toast.error({ fr: 'Erreur', ru: 'Ошибка', md: 'Eroare', uk: 'Помилка' }[language] || 'Erreur');
    }
  };

  const handleDeleteFuel = async (fuelId, e) => {
    if (e) e.stopPropagation();
    
    const confirmMsg = { 
      fr: 'Supprimer ce plein ?', 
      ru: 'Удалить эту заправку?', 
      md: 'Ștergeți această alimentare?',
      uk: 'Видалити це заправлення?'
    }[language] || 'Supprimer ce plein ?';
    
    if (!window.confirm(confirmMsg)) return;
    
    try {
      await axios.delete(`${API}/fuel/${fuelId}`);
      await fetchFuelStats();
      setShowFuelPhoto(null);
      toast.success({ fr: 'Plein supprimé', ru: 'Заправка удалена', md: 'Alimentare ștearsă', uk: 'Заправлення видалено' }[language] || 'Plein supprimé');
    } catch (error) {
      console.error('Delete fuel error:', error);
      toast.error({ fr: 'Erreur lors de la suppression', ru: 'Ошибка удаления', md: 'Eroare la ștergere', uk: 'Помилка видалення' }[language] || 'Erreur lors de la suppression');
    }
  };
  
  const handleFuelUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error(getText(language, 'Veuillez sélectionner une image', 'Выберите изображение', 'Selectați o imagine', 'Виберіть зображення'));
      return;
    }
    
    setUploadingFuel(true);

    try {
      // Compression avant envoi (voir compressPhotoFile) : upload court meme en
      // 4G de chantier, analyse plus rapide, plus d'abandon du telephone.
      const photoData = await compressPhotoFile(file);
      try {
        const response = await axios.post(`${API}/fuel/upload`, {
          photo_data: photoData,
          date: getLocalDateStr()
        }, { timeout: 120000 });

        // Refresh fuel stats
        await fetchFuelStats();

        toast.success(
          getText(language,
            `Ticket ajouté: ${response.data.liters}L - ${response.data.euros}€`,
            `Квитанция добавлена: ${response.data.liters}L - ${response.data.euros}€`,
            `Bon adăugat: ${response.data.liters}L - ${response.data.euros}€`,
            `Квитанцію додано: ${response.data.liters}L - ${response.data.euros}€`
          ),
          { duration: 5000 }
        );

        setShowFuelDialog(false);
      } catch (error) {
        toast.error(error.response?.data?.detail || t('error'));
      } finally {
        setUploadingFuel(false);
      }
    } catch (error) {
      // Echec AVANT l'envoi (preparation de la photo) : message explicite, et
      // surtout le spinner s'arrete, au lieu de tourner indefiniment.
      toast.error(getText(language,
        "Photo illisible, reprenez-la avec l'appareil photo",
        'Фото не читается, сделайте новый снимок камерой',
        'Fotografie ilizibilă, refaceți-o cu camera',
        'Фото не читається, зробіть новий знімок камерою'));
      setUploadingFuel(false);
    }

    // Reset input
    if (fuelGalleryRef.current) {
      fuelGalleryRef.current.value = '';
    }
    if (fuelCameraRef.current) {
      fuelCameraRef.current.value = '';
    }
  };

  // Handle camera button click - different behavior for mobile vs desktop
  const handleCameraClick = () => {
    if (isMobile) {
      // On mobile, use native camera via file input
      fuelCameraRef.current?.click();
    } else {
      // On desktop, use webcam API
      openWebcam();
    }
  };

  // Camera error state for showing instructions
  const [cameraError, setCameraError] = useState(null);
  
  // Detect Mac/Safari for specific instructions
  const isMac = /Macintosh|MacIntel|MacPPC|Mac68K/i.test(navigator.userAgent);
  const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
  
  // Webcam functions for desktop
  const openWebcam = async () => {
    setCameraError(null);
    
    // Check if browser supports getUserMedia
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      const errorMsg = { 
        fr: 'Votre navigateur ne supporte pas l\'accès à la caméra. Utilisez la galerie.',
        ru: 'Ваш браузер не поддерживает доступ к камере. Используйте галерею.',
        md: 'Browserul dvs. nu acceptă accesul la cameră. Folosiți galeria.',
        uk: 'Ваш браузер не підтримує доступ до камери. Використовуйте галерею.'
      }[language] || 'Votre navigateur ne supporte pas l\'accès à la caméra. Utilisez la galerie.';
      setCameraError({ type: 'not_supported', message: errorMsg });
      toast.error(errorMsg, { duration: 5000 });
      return;
    }
    
    try {
      // First try with environment facing camera (for laptops with multiple cameras)
      let stream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'environment', width: { ideal: 1280 }, height: { ideal: 720 } } 
        });
      } catch {
        // Fallback to any available camera with simpler constraints
        stream = await navigator.mediaDevices.getUserMedia({ 
          video: true 
        });
      }
      
      setWebcamStream(stream);
      setShowWebcam(true);
      setShowFuelDialog(false);
      setCameraError(null);
      
      // Wait for video element to be ready
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }
      }, 100);
    } catch (error) {
      console.error('Camera error:', error.name, error.message);
      
      let errorType = 'unknown';
      let errorMsg = '';
      let helpText = '';
      
      if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
        errorType = 'permission';
        if (isMac && isSafari) {
          errorMsg = language === 'ru' 
            ? 'Safari блокирует камеру.'
            : language === 'md' 
              ? 'Safari blochează camera.'
              : 'Safari bloque l\'accès à la caméra.';
          helpText = language === 'ru'
            ? 'Safari > Настройки > Веб-сайты > Камера > Разрешить для этого сайта'
            : language === 'md'
              ? 'Safari > Preferințe > Site-uri web > Cameră > Permite pentru acest site'
              : 'Safari > Préférences > Sites web > Caméra > Autoriser pour ce site';
        } else if (isMac) {
          errorMsg = language === 'ru' 
            ? 'Браузер блокирует камеру.'
            : language === 'md' 
              ? 'Browserul blochează camera.'
              : 'Le navigateur bloque l\'accès à la caméra.';
          helpText = language === 'ru'
            ? 'Нажмите на иконку камеры в адресной строке для разрешения'
            : language === 'md'
              ? 'Faceți clic pe pictograma camerei în bara de adrese pentru a permite'
              : 'Cliquez sur l\'icône de la caméra dans la barre d\'adresse pour autoriser';
        } else {
          errorMsg = language === 'ru' 
            ? 'Доступ к камере запрещён.'
            : language === 'md' 
              ? 'Acces la cameră refuzat.'
              : 'Accès à la caméra refusé.';
          helpText = language === 'ru'
            ? 'Разрешите доступ в настройках браузера'
            : language === 'md'
              ? 'Permiteți accesul în setările browserului'
              : 'Autorisez l\'accès dans les paramètres du navigateur';
        }
      } else if (error.name === 'NotFoundError' || error.name === 'DevicesNotFoundError') {
        errorType = 'not_found';
        errorMsg = language === 'ru' 
          ? 'Камера не обнаружена.'
          : language === 'md' 
            ? 'Camera nu a fost găsită.'
            : 'Aucune caméra détectée.';
        helpText = language === 'ru'
          ? 'Убедитесь, что камера подключена'
          : language === 'md'
            ? 'Asigurați-vă că camera este conectată'
            : 'Vérifiez que la caméra est connectée';
      } else if (error.name === 'NotReadableError' || error.name === 'TrackStartError') {
        errorType = 'in_use';
        errorMsg = language === 'ru' 
          ? 'Камера уже используется.'
          : language === 'md' 
            ? 'Camera este deja în uz.'
            : 'La caméra est déjà utilisée.';
        helpText = language === 'ru'
          ? 'Закройте другие приложения, использующие камеру'
          : language === 'md'
            ? 'Închideți alte aplicații care folosesc camera'
            : 'Fermez les autres applications utilisant la caméra';
      } else if (error.name === 'OverconstrainedError') {
        errorType = 'constraints';
        errorMsg = language === 'ru' 
          ? 'Камера не поддерживает требуемое разрешение.'
          : language === 'md' 
            ? 'Camera nu acceptă rezoluția cerută.'
            : 'La caméra ne supporte pas la résolution demandée.';
      } else {
        errorMsg = language === 'ru' 
          ? 'Impossible d\'ouvrir la caméra.'
          : language === 'md' 
            ? 'Imposibil de deschis camera.'
            : 'Impossible d\'ouvrir la caméra.';
        helpText = error.message || '';
      }
      
      setCameraError({ type: errorType, message: errorMsg, help: helpText });
      toast.error(errorMsg, { duration: 5000 });
    }
  };

  const closeWebcam = () => {
    if (webcamStream) {
      webcamStream.getTracks().forEach(track => track.stop());
      setWebcamStream(null);
    }
    setShowWebcam(false);
  };

  const capturePhoto = async () => {
    if (!videoRef.current || !canvasRef.current) return;
    
    const video = videoRef.current;
    const canvas = canvasRef.current;
    
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0);
    
    const photoData = canvas.toDataURL('image/jpeg', 0.8);
    
    // Close webcam
    closeWebcam();
    
    // Upload the photo
    setShowFuelDialog(true);
    setUploadingFuel(true);
    
    try {
      const response = await axios.post(`${API}/fuel/upload`, {
        photo_data: photoData,
        date: getLocalDateStr()
      });
      
      await fetchFuelStats();
      
      toast.success(
        getText(language,
          `Ticket ajouté: ${response.data.liters}L - ${response.data.euros}€`,
          `Квитанция добавлена: ${response.data.liters}L - ${response.data.euros}€`,
          `Bon adăugat: ${response.data.liters}L - ${response.data.euros}€`,
          `Квитанцію додано: ${response.data.liters}L - ${response.data.euros}€`
        ),
        { duration: 5000 }
      );
      
      setShowFuelDialog(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error'));
    } finally {
      setUploadingFuel(false);
    }
  };

  const handleCheckin = async () => {
    const today = getLocalDateStr();
    
    // Block check-in if yesterday's project is missing
    if (missingProjectRecord) {
      setShowMissingProjectDialog(true);
      toast.warning(getText(language,
        'Veuillez d\'abord sélectionner le chantier d\'hier',
        'Сначала выберите объект за вчера',
        'Selectați mai întâi șantierul de ieri',
        'Спочатку оберіть об\'єкт за вчора'
      ));
      return;
    }
    
    if (attendance.some(a => a.date === today)) {
      toast.info(t('alreadyCheckedIn'));
      return;
    }

    // On demande le chantier MAINTENANT, pre-rempli avec celui du dernier
    // pointage : le cas courant se valide en un seul appui.
    if (projects.length === 0 && !projectsLoading) fetchProjects();
    const lastUsed = attendance.find(a => a.project_id);
    setCheckinProjectId(lastUsed?.project_id || '');
    setCheckinProjectIds(lastUsed?.project_ids?.length ? lastUsed.project_ids : (lastUsed?.project_id ? [lastUsed.project_id] : []));
    setShowCheckinProjectDialog(true);
  };

  const doCheckin = async (projectIds) => {
    const ids = Array.isArray(projectIds) ? projectIds.filter(Boolean) : (projectIds ? [projectIds] : []);
    const today = getLocalDateStr();
    // Chantier OBLIGATOIRE (ordre Pavel 14/09/2026) : aucun pointage ne part
    // sans chantier, le serveur le refuse aussi (400).
    if (ids.length === 0) {
      toast.error(getText(language,
        'Le chantier est obligatoire pour pointer.',
        'Объект обязателен для отметки.',
        'Șantierul este obligatoriu pentru pontaj.',
        'Об\'єкт обов\'язковий для відмітки.'
      ));
      setShowCheckinProjectDialog(true);
      return;
    }
    setShowCheckinProjectDialog(false);
    setCheckingIn(true);
    const checkinPayload = {
      date: today,
      status: 'present',
      overtime_minutes: 0,
      has_travel: false,
      is_half_day: false,
      project_id: ids[0] || null,
      project_ids: ids
    };
    try {
      const response = await axios.post(`${API}/attendance/checkin`, checkinPayload);
      setAttendance([response.data, ...attendance]);
      toast.success(t('checkInSuccess'));
    } catch (error) {
      if (!error.response) {
        // Network down: queue the check-in, it will be sent when back online
        queueCheckin(user?.id, checkinPayload);
        const queuedMsg = getText(language,
          'Pas de réseau : votre pointage est enregistré et partira automatiquement au retour de la connexion.',
          'Нет сети: ваша отметка сохранена и отправится автоматически при появлении связи.',
          'Fără rețea: pontajul este salvat și va fi trimis automat când revine conexiunea.',
          'Немає мережі: вашу відмітку збережено, її буде надіслано автоматично, щойно з\'явиться зв\'язок.'
        );
        toast.info(queuedMsg, { duration: 8000 });
      } else {
        toast.error(error.response?.data?.detail || t('error'));
      }
    } finally {
      setCheckingIn(false);
    }
  };

  // Arrivee depuis le bouton "Pointer maintenant" de l'accueil (/attendance?pointer=1) :
  // on ouvre directement le choix du chantier, une seule fois, puis on nettoie l'URL.
  useEffect(() => {
    if (loading || pointerAutoRef.current) return;
    if (!new URLSearchParams(location.search).get('pointer')) return;
    pointerAutoRef.current = true;
    navigate('/attendance', { replace: true });
    if (isManager || isPreviewMode) return;
    handleCheckin();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, location.search]);

  const openOvertimeDialog = (record) => {
    setSelectedRecord(record);
    setOvertimeValue(record.overtime_minutes || 0);
    setHasTravelValue(record.has_travel || false);
    setIsHalfDayValue(record.is_half_day || false);
    setSelectedProjectId(record.project_id || '');
    setSelectedProjectIds(record.project_ids?.length ? record.project_ids : (record.project_id ? [record.project_id] : []));
    setShowOvertimeDialog(true);
  };

  const saveOvertime = async () => {
    if (!selectedRecord) return;
    
    try {
      // Heures sup retirees (Pavel 2026-08-08) : on n'envoie plus overtime_minutes.
      const updateData = { project_id: selectedProjectIds[0] || null, project_ids: selectedProjectIds, has_travel: hasTravelValue, is_half_day: isHalfDayValue };
      
      const response = await axios.put(`${API}/attendance/${selectedRecord.id}`, updateData);
      
      setAttendance(attendance.map(a => 
        a.id === selectedRecord.id ? response.data : a
      ));
      
      setShowOvertimeDialog(false);
      
      toast.success(getText(language,
        'Enregistré avec succès !',
        'Успешно сохранено!',
        'Salvat cu succes!',
        'Успішно збережено!'
      ), { duration: 4000 });
    } catch (error) {
      toast.error(t('error'));
    }
  };

  const formatOvertime = (minutes) => {
    if (!minutes || minutes === 0) return '-';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `+${hours}h${mins > 0 ? mins.toString().padStart(2, '0') : ''}`;
  };

  // Convert total days to seniority (months/years)
  const formatSeniority = (totalDays) => {
    if (!totalDays || totalDays === 0) return getText(language, '0 jour', '0 дней', '0 zile', '0 днів');
    
    // Assuming ~22 working days per month
    const workingDaysPerMonth = 22;
    const totalMonths = Math.floor(totalDays / workingDaysPerMonth);
    const remainingDays = totalDays % workingDaysPerMonth;
    
    const years = Math.floor(totalMonths / 12);
    const months = totalMonths % 12;
    
    if (years > 0 && months > 0) {
      return getText(language,
        `${years} an${years > 1 ? 's' : ''} et ${months} mois`,
        `${years} год${years > 1 ? (years < 5 ? 'а' : 'ов') : ''} и ${months} мес.`,
        `${years} an${years > 1 ? 'i' : ''} și ${months} luni`,
        `${years} рік${years > 1 ? (years < 5 ? 'и' : 'ів') : ''} і ${months} міс.`
      );
    } else if (years > 0) {
      return getText(language,
        `${years} an${years > 1 ? 's' : ''}`,
        `${years} год${years > 1 ? (years < 5 ? 'а' : 'ов') : ''}`,
        `${years} an${years > 1 ? 'i' : ''}`,
        `${years} рік${years > 1 ? (years < 5 ? 'и' : 'ів') : ''}`
      );
    } else if (months > 0) {
      return getText(language,
        `${months} mois`,
        `${months} мес.`,
        `${months} luni`,
        `${months} міс.`
      );
    } else {
      return getText(language,
        `${remainingDays} jour${remainingDays > 1 ? 's' : ''}`,
        `${remainingDays} дн.`,
        `${remainingDays} zile`,
        `${remainingDays} дн.`
      );
    }
  };

  const today = getLocalDateStr();
  const todayRecord = attendance.find(a => a.date === today);
  const isCheckedInToday = !!todayRecord;
  
  // Check if today's record already has overtime validated (LPC) or project (all)
  const hasOvertimeSubmitted = todayRecord && (
    todayRecord.project_id || todayRecord.overtime_validated
  );
  
  // Helper function to check if a record can be edited
  // For EBC: can always edit project_id
  // For LPC: only today's record AND not yet validated
  const canEditRecord = (record) => {
    if (isEBC) return true;
    if (record.date !== today) return false;
    // Heures sup retirees (Pavel 2026-08-08) : le verrou 'overtime_validated'
    // ne doit plus bloquer l'edition du chantier / demi-journee / deplacement.
    return true;
  };
  
  // Get the date-fns locale based on language
  const dateLocale = language === 'ru' ? ru : language === 'md' ? ro : language === 'uk' ? uk : fr;
  
  // Month names for selector
  const monthNames = language === 'ru' 
    ? ['Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь', 'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь']
    : language === 'md'
      ? ['Ianuarie', 'Februarie', 'Martie', 'Aprilie', 'Mai', 'Iunie', 'Iulie', 'August', 'Septembrie', 'Octombrie', 'Noiembrie', 'Decembrie']
      : language === 'uk'
        ? ['Січень', 'Лютий', 'Березень', 'Квітень', 'Травень', 'Червень', 'Липень', 'Серпень', 'Вересень', 'Жовтень', 'Листопад', 'Грудень']
        : ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
  
  // Filter attendance by selected month
  const filteredAttendance = attendance.filter(a => {
    const date = new Date(a.date);
    return date.getMonth() === filterMonth && date.getFullYear() === filterYear;
  });

  // Stats for filtered month
  const filteredOvertime = filteredAttendance.reduce((sum, a) => sum + (a.overtime_minutes || 0), 0);
  const filteredTravel = filteredAttendance.filter(a => a.has_travel).length;
  
  // Current month stats (for today's card)
  const currentMonth = new Date().getMonth();
  const currentYear = new Date().getFullYear();
  const thisMonthAttendance = attendance.filter(a => {
    const date = new Date(a.date);
    return date.getMonth() === currentMonth && date.getFullYear() === currentYear;
  });

  const totalOvertime = thisMonthAttendance.reduce((sum, a) => sum + (a.overtime_minutes || 0), 0);
  const totalTravel = thisMonthAttendance.filter(a => a.has_travel).length;
  const attendanceDates = attendance.map(a => new Date(a.date));

  // Build set of attended date strings for quick lookup
  const attendedDateSet = new Set(attendance.map(a => a.date));
  
  // Compute absent dates: past Mon-Sat without attendance, after user creation
  const absentDates = (() => {
    const result = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const userCreated = user?.created_at ? new Date(user.created_at) : null;
    if (userCreated) userCreated.setHours(0, 0, 0, 0);
    
    // Go back up to 12 months
    const startDate = new Date(today);
    startDate.setMonth(startDate.getMonth() - 12);
    if (userCreated && userCreated > startDate) startDate.setTime(userCreated.getTime());
    
    const d = new Date(startDate);
    while (d < today) {
      const dow = d.getDay();
      // Skip Sundays (0), keep Saturday (6) and weekdays (1-5)
      if (dow !== 0) {
        // Use local date components to avoid UTC timezone shift
        const dateStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        if (!attendedDateSet.has(dateStr)) {
          result.push(new Date(d));
        }
      }
      d.setDate(d.getDate() + 1);
    }
    return result;
  })();

  const locale = language === 'ru' ? 'ru-RU' : language === 'md' ? 'ro-RO' : language === 'uk' ? 'uk-UA' : 'fr-FR';

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div data-testid="attendance-page" className="space-y-6 animate-fadeIn">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="page-header mb-0">{t('attendanceTitle')}</h1>
      </div>

      {/* Missing project yesterday banner */}
      {missingProjectRecord && (
        <div 
          className="flex items-center gap-3 p-4 bg-red-50 border border-red-200 rounded-xl text-red-800 cursor-pointer hover:bg-red-100 transition-colors" 
          onClick={() => setShowMissingProjectDialog(true)}
          data-testid="missing-project-banner"
        >
          <AlertTriangle className="w-5 h-5 flex-shrink-0 text-red-600" />
          <p className="text-sm font-medium">
            {getText(language,
              'Chantier manquant pour hier ! Cliquez ici pour le sélectionner avant de pointer.',
              'Не указан объект за вчера! Нажмите, чтобы выбрать перед отметкой.',
              'Șantier lipsă pentru ieri! Apăsați pentru a selecta înainte de a puncta.',
              'Об\'єкт відсутній за вчора! Натисніть, щоб обрати перед відміткою.'
            )}
          </p>
        </div>
      )}

      {/* Birthday reminder banner */}
      {!user?.birthday && (
        <div className="flex items-center gap-3 p-4 bg-amber-50 border border-amber-200 rounded-xl text-amber-800" data-testid="birthday-reminder-banner">
          <CalendarDays className="w-5 h-5 flex-shrink-0" />
          <p className="text-sm font-medium">
            {getText(language,
              'N\'oubliez pas d\'ajouter votre date d\'anniversaire dans votre Profil !',
              'Не забудьте добавить дату рождения в вашем Профиле!',
              'Nu uitați să adăugați data de naștere în Profilul dvs.!',
              'Не забудьте додати дату народження у вашому Профілі!'
            )}
          </p>
          <a href="/profile" className="ml-auto text-xs font-semibold text-amber-700 bg-amber-100 hover:bg-amber-200 px-3 py-1.5 rounded-lg transition-colors whitespace-nowrap">
            {getText(language, 'Mon Profil', 'Мой Профиль', 'Profilul meu', 'Мій Профіль')}
          </a>
        </div>
      )}

      {/* Salary Card */}
      <SalaryCard />
      
      {/* Today's Status */}
      <div className={`card-base p-6 ${isCheckedInToday ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'}`}>
        <div className="flex flex-col sm:flex-row items-center gap-4">
          <button
            onClick={!isCheckedInToday && !checkingIn ? handleCheckin : undefined}
            disabled={isCheckedInToday || checkingIn}
            className={`w-16 h-16 rounded-xl flex items-center justify-center transition-all ${
              isCheckedInToday 
                ? 'bg-emerald-500 cursor-default' 
                : 'bg-amber-500 cursor-pointer hover:bg-amber-600 hover:scale-105 active:scale-95'
            }`}
            data-testid="status-icon-btn"
            title={!isCheckedInToday ? getText(language, 'Cliquez pour pointer', 'Нажмите для отметки', 'Apăsați pentru a puncta', 'Натисніть для відмітки') : ''}
          >
            {checkingIn ? (
              <div className="spinner w-8 h-8 border-white border-t-transparent" />
            ) : isCheckedInToday ? (
              <CheckCircle2 className="w-8 h-8 text-white" />
            ) : (
              <CalendarDays className="w-8 h-8 text-white" />
            )}
          </button>
          <div className="flex-1 text-center sm:text-left">
            <h2 className="text-xl font-bold" style={{ color: 'var(--color-primary)' }}>
              {isCheckedInToday ? t('checkedToday') : t('notCheckedToday')}
            </h2>
            <p className="text-gray-600">
              {new Date().toLocaleDateString(locale, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
            </p>
            {isCheckedInToday && (
              <div className="flex flex-wrap gap-2 mt-2">
                {/* LPC: Show overtime and travel badges */}
                {!isEBC && todayRecord.overtime_minutes > 0 && (
                  <span className="badge badge-info">
                    <Clock className="w-3 h-3 mr-1" />
                    {formatOvertime(todayRecord.overtime_minutes)}
                  </span>
                )}
                {/* LPC: Show travel badge */}
                {!isEBC && todayRecord.has_travel && (
                  <span className="badge badge-warning">
                    <Car className="w-3 h-3 mr-1" />
                    {getText(language, 'Déplacement', 'Командировка', 'Deplasare', 'Відрядження')}
                  </span>
                )}
                {/* All: Show project name badge */}
                {todayRecord.project_name && (
                  <span className="badge bg-emerald-100 text-emerald-700">
                    <MapPin className="w-3 h-3 mr-1" />
                    {(todayRecord.project_names?.length ? todayRecord.project_names : [todayRecord.project_name]).filter(Boolean).join(' + ')}
                  </span>
                )}
                {/* EBC: Show travel badge */}
                {isEBC && todayRecord.has_travel && (
                  <span className="badge badge-warning">
                    <Car className="w-3 h-3 mr-1" />
                    {getText(language, 'Déplacement', 'Командировка', 'Deplasare', 'Відрядження')}
                  </span>
                )}
                {/* EBC: Show half-day badge */}
                {isEBC && todayRecord.is_half_day && (
                  <span className="badge bg-purple-100 text-purple-700">
                    ½ {getText(language, 'Demi-journée', 'Полдня', 'Jumătate de zi', 'Половина дня')}
                  </span>
                )}
              </div>
            )}
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            {!isCheckedInToday ? (
              <Button
                onClick={() => handleCheckin()}
                disabled={checkingIn}
                className="btn-primary"
                data-testid="checkin-btn"
              >
                {checkingIn ? <div className="spinner w-4 h-4" /> : t('checkInButton')}
              </Button>
            ) : hasOvertimeSubmitted ? (
              <Button
                onClick={() => openOvertimeDialog(todayRecord)}
                className="btn-secondary flex items-center gap-2"
                data-testid="overtime-submitted-btn"
              >
                <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                {todayRecord.project_name
                  ? getText(language, 'Modifier chantier', 'Изменить объект', 'Modifică șantier', 'Змінити об\'єкт')
                  : isEBC
                    ? getText(language, 'Choisir chantier', 'Выбрать объект', 'Alege șantier', 'Вибрати об\'єкт')
                    : getText(language, 'Détails validés', 'Часы учтены', 'Ore înregistrate', 'Години враховані')
                }
              </Button>
            ) : (
              <Button
                onClick={() => openOvertimeDialog(todayRecord)}
                className="btn-secondary flex items-center gap-2"
                data-testid="add-overtime-btn"
              >
                <MapPin className="w-4 h-4" />
                {getText(language, 'Chantier / Détails', 'Объект / Детали', 'Șantier / Detalii', 'Об\'єкт / Деталі')}
              </Button>
            )}
          </div>
        </div>
      </div>
      
      {/* Calendar with Stats */}
      <div className="card-base p-6">
        <h3 className="section-title mb-4">{t('presenceCalendar')}</h3>
        
        {/* Stats cards above calendar */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          <div className="stat-card">
            <span className="stat-value" style={{ color: 'var(--color-primary)' }}>{filteredAttendance.length}</span>
            <span className="stat-label">
              {getText(language, 'Jours ce mois', 'Дней за месяц', 'Zile luna aceasta', 'Днів цього місяця')}
            </span>
          </div>
          <div className="stat-card bg-gradient-to-r from-emerald-50 to-teal-50 border-emerald-200">
            <span className="stat-value text-emerald-600">{formatSeniority(attendance.length)}</span>
            <span className="stat-label">
              {getText(language, 'Ancienneté', 'Стаж', 'Vechime', 'Стаж')}
            </span>
          </div>
        </div>
        
        <div className="flex justify-center">
          <Calendar
            mode="single"
            selected={selectedDate}
            onSelect={setSelectedDate}
            month={calendarMonth}
            onMonthChange={setCalendarMonth}
            locale={dateLocale}
            weekStartsOn={1}
            modifiers={{ 
              attended: attendanceDates,
              absent: absentDates,
              sunday: (date) => date.getDay() === 0
            }}
            modifiersStyles={{
              attended: {
                backgroundColor: 'rgb(16 185 129)',
                color: 'white',
                fontWeight: 'bold'
              },
              absent: {
                backgroundColor: 'rgb(254 226 226)',
                color: 'rgb(185 28 28)',
                fontWeight: 'normal'
              },
              sunday: {
                backgroundColor: '#e5e7eb',
                color: '#9ca3af',
                fontWeight: 'normal'
              }
            }}
            className="rounded-md border"
            style={{ borderColor: 'var(--color-border)' }}
            data-testid="attendance-calendar"
          />
        </div>
        <div className="flex items-center justify-center gap-4 mt-4 text-sm flex-wrap">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-emerald-500 rounded" />
            <span className="text-gray-600">{t('present')}</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-red-100 border border-red-300 rounded" />
            <span className="text-gray-600">{getText(language, 'Absent', 'Отсутствие', 'Absent', 'Відсутність')}</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-gray-200 rounded" />
            <span className="text-gray-600">{getText(language, 'Dimanche', 'Воскресенье', 'Duminică', 'Неділя')}</span>
          </div>
        </div>
      </div>
      
      {/* History + Salary Breakdown */}
      <div className="card-base p-6">
        {/* Collapsible History Header */}
        <button 
          onClick={() => setHistoryOpen(!historyOpen)}
          className="w-full flex items-center justify-between mb-2"
          data-testid="toggle-history-btn"
        >
          <h3 className="section-title mb-0">{t('recentHistory')}</h3>
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <span>{filteredAttendance.length} {getText(language, 'jours', 'дней', 'zile', 'днів')}</span>
            {historyOpen ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </div>
        </button>
        
        {/* Collapsible History Content */}
        {historyOpen && (
          attendance.length === 0 ? (
            <div className="empty-state mt-4">
              <CalendarDays className="empty-state-icon" />
              <p className="empty-state-text">{t('noAttendance')}</p>
            </div>
          ) : (
            <div className="space-y-2 max-h-96 overflow-y-auto mt-4">
              {attendance.slice(0, 15).map((record, index) => (
                <div 
                  key={record.id} 
                  className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl animate-slideIn cursor-pointer hover:bg-gray-100"
                  style={{ animationDelay: `${index * 0.03}s` }}
                  onClick={() => openOvertimeDialog(record)}
                >
                  <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                  <div className="flex-1">
                    <span className="font-medium" style={{ color: 'var(--color-text)' }}>
                      {new Date(record.date).toLocaleDateString(locale, { weekday: 'long', day: 'numeric', month: 'long' })}
                    </span>
                    <div className="flex flex-wrap gap-2 mt-1">
                      {!isEBC && record.overtime_minutes > 0 && (
                        <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full">
                          {formatOvertime(record.overtime_minutes)}
                        </span>
                      )}
                      {!isEBC && record.has_travel && (
                        <span className="text-xs px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full flex items-center gap-1">
                          <Car className="w-3 h-3" />
                          {getText(language, 'Dépl.', 'Команд.', 'Depl.', 'Відр.')}
                        </span>
                      )}
                      {record.project_name && (
                        <span className="text-xs px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded-full flex items-center gap-1">
                          <MapPin className="w-3 h-3" />
                          {(record.project_names?.length ? record.project_names : [record.project_name]).filter(Boolean).join(' + ')}
                        </span>
                      )}
                      {!record.project_name && (
                        <span className="text-xs px-2 py-0.5 bg-gray-200 text-gray-500 rounded-full flex items-center gap-1">
                          <MapPin className="w-3 h-3" />
                          {getText(language, 'Aucun chantier', 'Без объекта', 'Fără șantier', 'Без об\'єкта')}
                        </span>
                      )}
                      {isEBC && record.has_travel && (
                        <span className="text-xs px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full flex items-center gap-1">
                          <Car className="w-3 h-3" />
                          {getText(language, 'Dépl.', 'Команд.', 'Depl.', 'Відр.')}
                        </span>
                      )}
                      {isEBC && record.is_half_day && (
                        <span className="text-xs px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full">
                          ½ {getText(language, 'Demi-j.', 'Полдня', 'Jum. zi', 'Півд.')}
                        </span>
                      )}
                    </div>
                  </div>
                  <button className="text-gray-400 hover:text-gray-600 p-1">
                    <MapPin className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )
        )}

        {/* Detailed Salary Breakdown */}
        {salaryDetail && salaryDetail.daily_rate > 0 && (
          <div className="mt-6 border-t border-gray-200 pt-6" data-testid="salary-breakdown">
            <div className="flex items-center gap-2 mb-4">
              <Banknote className="w-5 h-5 text-slate-700" />
              <h4 className="text-base font-bold text-slate-800">
                {getText(language, 'Détail du salaire', 'Детали зарплаты', 'Detalii salariu', 'Деталі зарплати')} — {monthNames[filterMonth]}
              </h4>
            </div>
            
            <div className="space-y-2">
              {/* Base salary */}
              <div className="flex items-center justify-between py-2 px-3 bg-slate-50 rounded-lg">
                <span className="text-sm text-slate-600 flex items-center gap-2">
                  <CalendarDays className="w-4 h-4" />
                  {getText(language, 'Salaire de base', 'Базовый оклад', 'Salariu de bază', 'Базовий оклад')}
                </span>
                <div className="text-right">
                  <span className="text-sm font-bold text-slate-800">{salaryDetail.gross_salary.toFixed(2)}€</span>
                  <span className="text-xs text-slate-400 ml-1">
                    ({salaryDetail.days_worked}{salaryDetail.half_days > 0 ? getText(language, 'j (dont ', 'д (из них ', 'z (din care ', 'д (з них ') + salaryDetail.half_days + '×½)' : 'j'} × {salaryDetail.daily_rate}€)
                  </span>
                </div>
              </div>

              {/* Overtime - LPC only */}
              {salaryDetail.company !== 'Eco Bati Construction' && salaryDetail.total_overtime_minutes > 0 && (
                <div className="flex items-center justify-between py-2 px-3 bg-blue-50 rounded-lg border border-blue-200">
                  <span className="text-sm text-blue-700 flex items-center gap-2 font-semibold">
                    <Clock className="w-4 h-4" />
                    {getText(language, 'Heures supplémentaires', 'Сверхурочные', 'Ore suplimentare', 'Понаднормові')}
                  </span>
                  <div className="text-right">
                    <span className="text-sm font-bold text-blue-700">+{salaryDetail.overtime_cost.toFixed(2)}€</span>
                    <span className="text-xs text-blue-400 ml-1">
                      ({Math.floor(salaryDetail.total_overtime_minutes / 60)}h{(salaryDetail.total_overtime_minutes % 60).toString().padStart(2, '0')} × {salaryDetail.overtime_rate}€/h)
                    </span>
                  </div>
                </div>
              )}

              {/* Bonuses - show only if > 0 */}
              {salaryDetail.total_bonus > 0 && (
                <div className="flex items-center justify-between py-2 px-3 bg-green-50 rounded-lg border border-green-200">
                  <span className="text-sm text-green-700 flex items-center gap-2 font-semibold">
                    <Award className="w-4 h-4" />
                    {getText(language, 'Primes', 'Премии', 'Prime', 'Премії')}
                  </span>
                  <span className="text-sm font-bold text-green-700">+{salaryDetail.total_bonus.toFixed(2)}€</span>
                </div>
              )}

              {/* Penalties - show only if > 0 */}
              {salaryDetail.total_penalties > 0 && (
                <div className="flex items-center justify-between py-2 px-3 bg-red-50 rounded-lg border border-red-200">
                  <span className="text-sm text-red-700 flex items-center gap-2 font-semibold">
                    <AlertTriangle className="w-4 h-4" />
                    {getText(language, 'Pénalités', 'Штрафы', 'Penalități', 'Штрафи')}
                  </span>
                  <span className="text-sm font-bold text-red-700">-{salaryDetail.total_penalties.toFixed(2)}€</span>
                </div>
              )}

              {/* Advances (acomptes) - show only if > 0 */}
              {salaryDetail.total_advances > 0 && (
                <div className="flex items-center justify-between py-2 px-3 bg-amber-50 rounded-lg border border-amber-200">
                  <span className="text-sm text-amber-700 flex items-center gap-2 font-semibold">
                    <Wallet className="w-4 h-4" />
                    {getText(language, 'Acomptes', 'Авансы', 'Avansuri', 'Аванси')}
                  </span>
                  <span className="text-sm font-bold text-amber-700">-{salaryDetail.total_advances.toFixed(2)}€</span>
                </div>
              )}

              {/* Fuel deduction - show only if > 0 */}
              {salaryDetail.total_fuel > 0 && (
                <div className="flex items-center justify-between py-2 px-3 bg-orange-50 rounded-lg">
                  <span className="text-sm text-orange-700 flex items-center gap-2">
                    <Fuel className="w-4 h-4" />
                    {getText(language, 'Pleins carburant', 'Заправки', 'Alimentări', 'Заправки')}
                  </span>
                  <span className="text-sm font-bold text-orange-700">-{salaryDetail.total_fuel.toFixed(2)}€</span>
                </div>
              )}

              {/* Expenses deduction - show only if > 0 */}
              {salaryDetail.total_expenses > 0 && (
                <div className="flex items-center justify-between py-2 px-3 bg-purple-50 rounded-lg">
                  <span className="text-sm text-purple-700 flex items-center gap-2">
                    <Receipt className="w-4 h-4" />
                    {getText(language, 'Notes de frais', 'Расходы', 'Note de cheltuieli', 'Витрати')}
                  </span>
                  <span className="text-sm font-bold text-purple-700">-{salaryDetail.total_expenses.toFixed(2)}€</span>
                </div>
              )}

              {/* NET TOTAL */}
              <div className="border-t-2 border-dashed border-slate-300 pt-3 mt-3">
                <div className="flex items-center justify-between px-3">
                  <span className="text-base font-bold text-slate-800 flex items-center gap-2">
                    <TrendingUp className="w-5 h-5" />
                    {getText(language, 'SALAIRE NET', 'ЗАРПЛАТА НЕТТО', 'SALARIU NET', 'ЗАРПЛАТА НЕТТО')}
                  </span>
                  <span className={`text-xl font-bold ${salaryDetail.net_salary >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                    {salaryDetail.net_salary.toFixed(2)}€
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* Fuel/Carburant Section */}
      <div className="card-base p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <h3 className="section-title mb-0 flex items-center gap-2">
            <Fuel className="w-5 h-5" />
            {getText(language, 'Dépenses carburant', 'Расходы на топливо', 'Cheltuieli carburant', 'Витрати на паливо')}
          </h3>
          <Button
            onClick={() => setShowFuelDialog(true)}
            className="btn-primary flex items-center gap-2"
            data-testid="add-fuel-btn"
          >
            <Upload className="w-4 h-4" />
            {getText(language, 'Ajouter un ticket', 'Добавить чек', 'Adaugă bon', 'Додати чек')}
          </Button>
        </div>
        
        {/* Fuel Stats for selected month */}
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
          <div className="stat-card bg-orange-50 border-orange-200">
            <span className="stat-value text-orange-600">
              {fuelStats?.total_euros?.toFixed(2) || '0.00'}€
            </span>
            <span className="stat-label">
              {getText(language, 'Total du mois', 'Всего за месяц', 'Total pe lună', 'Всього за місяць')}
            </span>
          </div>
          <div className="stat-card">
            <span className="stat-value" style={{ color: 'var(--color-primary)' }}>
              {fuelStats?.total_liters?.toFixed(1) || '0'}L
            </span>
            <span className="stat-label">
              {getText(language, 'Litres', 'Литров', 'Litri', 'Літрів')}
            </span>
          </div>
          <div className="stat-card">
            <span className="stat-value text-gray-600">{fuelStats?.count || 0}</span>
            <span className="stat-label">
              {getText(language, 'Pleins', 'Заправок', 'Alimentări', 'Заправок')}
            </span>
          </div>
        </div>
        
        {/* Fuel Records List */}
        {fuelStats?.records?.length > 0 ? (
          <div className="space-y-3 max-h-64 overflow-y-auto">
            <h4 className="text-sm font-semibold text-gray-500">
              {getText(language, 'Détails pour', 'Детали за', 'Detalii pentru', 'Деталі за')} {monthNames[filterMonth]} {filterYear}
            </h4>
            {fuelStats.records.map((record, index) => (
              <div 
                key={record.id} 
                className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl animate-slideIn cursor-pointer hover:bg-gray-100 transition-colors"
                style={{ animationDelay: `${index * 0.03}s` }}
                onClick={() => setShowFuelPhoto(record)}
                data-testid={`fuel-record-${record.id}`}
              >
                <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center">
                  <Fuel className="w-5 h-5 text-orange-600" />
                </div>
                <div className="flex-1">
                  <span className="font-medium" style={{ color: 'var(--color-text)' }}>
                    {new Date(record.date).toLocaleDateString(locale, { weekday: 'short', day: 'numeric', month: 'short' })}
                  </span>
                  <div className="flex gap-2 mt-1">
                    <span className="text-xs px-2 py-0.5 bg-orange-100 text-orange-700 rounded-full font-semibold">
                      {record.euros.toFixed(2)}€
                    </span>
                    <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full">
                      {record.liters.toFixed(1)}L
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <div className="p-2 text-gray-400">
                    <Eye className="w-4 h-4" />
                  </div>
                  {isManager && (
                    <button
                      onClick={(e) => handleDeleteFuel(record.id, e)}
                      className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                      data-testid={`delete-fuel-${record.id}`}
                      title={getText(language, 'Supprimer', 'Удалить', 'Șterge', 'Видалити')}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            <Fuel className="w-12 h-12 mx-auto mb-2 text-gray-300" />
            <p>{getText(language, 'Aucune dépense de carburant ce mois', 'Нет данных о топливе за этот месяц', 'Fără date carburant pentru această lună', 'Немає даних про паливо за цей місяць')}</p>
          </div>
        )}
      </div>
      
      {/* Note de Frais Section */}
      <div className="card-base p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="section-title mb-0 flex items-center gap-2">
            <Receipt className="w-5 h-5" />
            {getText(language, 'Note de Frais', 'Расходы', 'Notă de cheltuieli', 'Витрати')}
          </h3>
          <Button
            onClick={() => setShowExpenseDialog(true)}
            className="btn-primary"
            data-testid="add-expense-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            {getText(language, 'Ajouter', 'Добавить', 'Adaugă', 'Додати')}
          </Button>
        </div>
        
        {/* Expense Stats */}
        <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl p-4 mb-4">
          <div className="flex items-center justify-around">
            <div className="text-center">
              <span className="stat-value text-purple-600">{expenseStats?.count || 0}</span>
              <span className="stat-label block text-xs text-purple-800">
                {getText(language, 'Notes', 'Записи', 'Note', 'Записи')}
              </span>
            </div>
            <div className="text-center">
              <span className="stat-value text-green-600">{expenseStats?.total_amount?.toFixed(2) || '0.00'}€</span>
              <span className="stat-label block text-xs text-green-800">
                {getText(language, 'Total', 'Всего', 'Total', 'Всього')}
              </span>
            </div>
          </div>
        </div>
        
        {/* Expense List */}
        {expenseStats?.records?.length > 0 ? (
          <div className="space-y-3 max-h-60 overflow-y-auto">
            {expenseStats.records.map((record) => (
              <div
                key={record.id}
                className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors"
              >
                {record.has_photo ? (
                  <button
                    onClick={() => setShowExpensePhoto(record)}
                    className="w-14 h-14 rounded-lg flex-shrink-0 bg-purple-100 flex items-center justify-center hover:opacity-80 transition-opacity"
                    title={getText(language, 'Voir le justificatif', 'Посмотреть квитанцию', 'Vezi justificativul', 'Переглянути квитанцію')}
                  >
                    <Receipt className="w-6 h-6 text-purple-600" />
                  </button>
                ) : (
                  // Depense sans justificatif : vignette neutre, pas d'image cassee.
                  <div className="w-14 h-14 rounded-lg flex-shrink-0 bg-gray-200 flex items-center justify-center">
                    <Receipt className="w-6 h-6 text-gray-400" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-gray-800 truncate">{record.title}</p>
                  <p className="text-sm">
                    <span className="font-semibold text-green-600">{record.total_amount?.toFixed(2) || '0.00'}€</span>
                  </p>
                  <p className="text-xs text-gray-400">
                    {new Date(record.date).toLocaleDateString(locale)}
                  </p>
                </div>
                <button
                  onClick={() => handleDeleteExpense(record.id)}
                  className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                >
                  <Trash2 className="w-5 h-5" />
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            <Receipt className="w-12 h-12 mx-auto mb-2 text-gray-300" />
            <p>{getText(language, 'Aucune note de frais ce mois', 'Нет расходов за этот месяц', 'Nicio notă de cheltuieli luna aceasta', 'Немає витрат за цей місяць')}</p>
          </div>
        )}
      </div>

      {/* Overtime/Travel Dialog + Project Selection Dialog */}
      <Dialog open={showOvertimeDialog} onOpenChange={setShowOvertimeDialog}>
        <DialogContent className="sm:max-w-md max-h-[90dvh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold" style={{ color: 'var(--color-primary)' }}>
              {getText(language, 'Détails du jour', 'Детали дня', 'Detalii zilei', 'Деталі дня')}
            </DialogTitle>
          </DialogHeader>
          
          {selectedRecord && (
            <div className="space-y-6 py-4">
              <p className="text-gray-600 text-center">
                {new Date(selectedRecord.date).toLocaleDateString(locale, { weekday: 'long', day: 'numeric', month: 'long' })}
              </p>
              
              {/* Jour passé verrouillé (heures sup retirées Pavel 2026-08-08, plus de mention d'heures sup) */}
              {!isEBC && !canEditRecord(selectedRecord) && (
                <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700">
                  <Lock className="w-5 h-5 flex-shrink-0" />
                  <span className="text-sm font-medium">
                    {getText(language, 'Ce jour est verrouillé et ne peut pas être modifié', 'Этот день заблокирован для редактирования', 'Această zi este blocată pentru editare', 'Цей день заблоковано для редагування')}
                  </span>
                </div>
              )}
              
              {/* ==================== ALL: Project Selector ==================== */}
              <div>
                <label className="block text-sm font-semibold mb-3" style={{ color: 'var(--color-text)' }}>
                  <MapPin className="w-4 h-4 inline mr-2" />
                  {getText(language, 'Sur quel chantier avez-vous travaillé ?', 'На каком объекте вы работали?', 'La ce șantier ați lucrat?', 'На якому об\'єкті ви працювали?')}
                </label>
                <div className="space-y-1 max-h-[40vh] overflow-y-auto" data-testid="project-selector">
                  {groupProjects(projects, language).map(group => (
                  <div key={group.key} className="space-y-1">
                  <p className="text-xs font-bold uppercase tracking-wide text-slate-500 pt-2">{group.label}</p>
                  {group.items.map(project => {
                    const on = selectedProjectIds.includes(project.id);
                    return (
                      <label key={project.id} className={`flex items-center gap-3 px-3 py-2 rounded-xl border cursor-pointer ${on ? 'bg-emerald-50 border-emerald-400 text-emerald-800 font-semibold' : 'bg-white border-gray-200'}`}>
                        <input
                          type="checkbox"
                          className="w-4 h-4 accent-emerald-600"
                          checked={on}
                          onChange={() => {
                            const next = on ? selectedProjectIds.filter(x => x !== project.id) : [...selectedProjectIds, project.id];
                            setSelectedProjectIds(next);
                            setSelectedProjectId(next[0] || '');
                          }}
                        />
                        <span>{project.name}</span>
                      </label>
                    );
                  })}
                  </div>
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  {getText(language, 'Cochez tous les chantiers de la journée.', 'Отметьте все объекты за день.', 'Bifați toate șantierele zilei.', 'Позначте всі об\'єкти за день.')}
                </p>
                
                {/* Current selection display */}
                {selectedProjectId && selectedProjectId !== 'none' && (
                  <div className="mt-3 p-3 bg-emerald-50 border border-emerald-200 rounded-xl">
                    <p className="text-sm text-emerald-700 font-medium flex items-center gap-1">
                      <CheckCircle2 className="w-4 h-4" />
                      {projects.find(p => p.id === selectedProjectId)?.name}
                    </p>
                  </div>
                )}
              </div>
              
              {/* ==================== LPC: Overtime Selector (retire Pavel 2026-08-08, remplace par les primes) ==================== */}
              {false && !isEBC && (
                <div className={!canEditRecord(selectedRecord) ? 'opacity-50 pointer-events-none' : ''}>
                  <label className="block text-sm font-semibold mb-3" style={{ color: 'var(--color-text)' }}>
                    <Clock className="w-4 h-4 inline mr-2" />
                    {getText(language, 'Heures supplémentaires', 'Сверхурочные (мин)', 'Ore suplimentare', 'Понаднормові (хв)')}
                  </label>
                  <div className="flex items-center justify-center gap-4">
                    <button
                      onClick={() => setOvertimeValue(Math.max(0, overtimeValue - 30))}
                      className="w-12 h-12 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors"
                      disabled={overtimeValue === 0}
                    >
                      <Minus className="w-5 h-5" />
                    </button>
                    
                    <div className="text-center min-w-[120px]">
                      <span className="text-4xl font-bold" style={{ color: 'var(--color-primary)' }}>
                        {Math.floor(overtimeValue / 60)}h{(overtimeValue % 60).toString().padStart(2, '0')}
                      </span>
                      <p className="text-sm text-gray-500 mt-1">
                        {overtimeValue === 0 
                          ? getText(language, 'Pas d\'heures sup.', 'Без сверхурочных', 'Fără ore sup.', 'Без понаднормових')
                          : getText(language, 'supplémentaires', 'сверхурочных', 'ore suplimentare', 'понаднормових')}
                      </p>
                    </div>
                    
                    <button
                      onClick={() => setOvertimeValue(overtimeValue + 30)}
                      className="w-12 h-12 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors"
                    >
                      <Plus className="w-5 h-5" />
                    </button>
                  </div>
                  
                  {/* Quick Select Buttons */}
                  <div className="flex flex-wrap justify-center gap-2 mt-4">
                    {OVERTIME_OPTIONS.map(opt => (
                      <button
                        key={opt}
                        onClick={() => setOvertimeValue(opt)}
                        className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                          overtimeValue === opt 
                            ? 'bg-[#0E2147] text-white' 
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        {opt === 0 ? '0' : `+${Math.floor(opt/60)}h${opt%60 > 0 ? (opt%60).toString().padStart(2,'0') : ''}`}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              
              {/* ==================== Déplacement (LPC + EBC, Pavel 2026-08-08) ==================== */}
              {true && (
                <div className={!canEditRecord(selectedRecord) ? 'opacity-50 pointer-events-none' : ''}>
                  <label className="block text-sm font-semibold mb-3" style={{ color: 'var(--color-text)' }}>
                    <Car className="w-4 h-4 inline mr-2" />
                    {getText(language, 'Déplacement (prime)', 'Командировка (надбавка)', 'Deplasare (primă)', 'Відрядження (надбавка)')}
                  </label>
                  <button
                    onClick={() => setHasTravelValue(!hasTravelValue)}
                    className={`w-full p-4 rounded-xl border-2 flex items-center justify-between transition-all ${
                      hasTravelValue 
                        ? 'bg-amber-50 border-amber-400' 
                        : 'bg-gray-50 border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        hasTravelValue ? 'bg-amber-500' : 'bg-gray-200'
                      }`}>
                        <Car className={`w-5 h-5 ${hasTravelValue ? 'text-white' : 'text-gray-500'}`} />
                      </div>
                      <span className={`font-medium ${hasTravelValue ? 'text-amber-700' : 'text-gray-600'}`}>
                        {getText(language, 'Déplacement', 'Командировка', 'Deplasare', 'Відрядження')}
                      </span>
                    </div>
                    <div className={`w-12 h-7 rounded-full p-1 transition-colors ${
                      hasTravelValue ? 'bg-amber-500' : 'bg-gray-300'
                    }`}>
                      <div className={`w-5 h-5 rounded-full bg-white shadow transform transition-transform ${
                        hasTravelValue ? 'translate-x-5' : 'translate-x-0'
                      }`} />
                    </div>
                  </button>
                </div>
              )}
              
              {/* ==================== Demi-journée (LPC + EBC, Pavel 2026-08-08) ==================== */}
              {true && (
                <div className={!canEditRecord(selectedRecord) ? 'opacity-50 pointer-events-none' : ''}>
                  <label className="block text-sm font-semibold mb-3" style={{ color: 'var(--color-text)' }}>
                    ½ {getText(language, 'Demi-journée', 'Полдня', 'Jumătate de zi', 'Пів дня')}
                  </label>
                  <button
                    onClick={() => setIsHalfDayValue(!isHalfDayValue)}
                    className={`w-full p-4 rounded-xl border-2 flex items-center justify-between transition-all ${
                      isHalfDayValue 
                        ? 'bg-orange-50 border-orange-400' 
                        : 'bg-gray-50 border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center text-lg font-bold ${
                        isHalfDayValue ? 'bg-orange-500 text-white' : 'bg-gray-200 text-gray-500'
                      }`}>
                        ½
                      </div>
                      <span className={`font-medium ${isHalfDayValue ? 'text-orange-700' : 'text-gray-600'}`}>
                        {getText(language, 'Demi-journée de travail', 'Полдня работы', 'Jumătate de zi de lucru', 'Пів дня роботи')}
                      </span>
                    </div>
                    <div className={`w-12 h-7 rounded-full p-1 transition-colors ${
                      isHalfDayValue ? 'bg-orange-500' : 'bg-gray-300'
                    }`}>
                      <div className={`w-5 h-5 rounded-full bg-white shadow transform transition-transform ${
                        isHalfDayValue ? 'translate-x-5' : 'translate-x-0'
                      }`} />
                    </div>
                  </button>
                </div>
              )}
              
              {/* Save Button (editable) or Close Button (locked) */}
              {!isEBC && !canEditRecord(selectedRecord) ? (
                <Button
                  onClick={() => setShowOvertimeDialog(false)}
                  className="btn-primary w-full"
                  data-testid="close-overtime-btn"
                >
                  {getText(language, 'Fermer', 'Закрыть', 'Închide', 'Закрити')}
                </Button>
              ) : (
                <Button
                  onClick={saveOvertime}
                  className="btn-primary w-full"
                  data-testid="save-overtime-btn"
                >
                  {getText(language, 'Enregistrer', 'Сохранить', 'Salvează', 'Зберегти')}
                </Button>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Fuel Upload Dialog */}
      <Dialog open={showFuelDialog} onOpenChange={(open) => {
        setShowFuelDialog(open);
        if (!open) setCameraError(null);
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold" style={{ color: 'var(--color-primary)' }}>
              {getText(language, 'Ajouter un ticket de carburant', 'Добавить чек на топливо', 'Adaugă bon carburant', 'Додати чек на паливо')}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            <div className="text-center">
              <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-orange-100 flex items-center justify-center">
                <Fuel className="w-10 h-10 text-orange-600" />
              </div>
              <p className="text-gray-600 mb-4">
                {getText(language,
                  'Prenez en photo votre ticket de station-service. L\'IA détectera automatiquement les litres et le montant.',
                  'Сфотографируйте чек с заправки. ИИ автоматически определит литры и сумму.',
                  'Fotografiați bonul de la benzinărie. AI-ul va detecta automat litrii și suma.',
                  'Сфотографуйте чек з заправки. ШІ автоматично визначить літри та суму.'
                )}
              </p>
            </div>
            
            {uploadingFuel ? (
              <div className="flex flex-col items-center justify-center py-8">
                <Loader2 className="w-12 h-12 text-orange-500 animate-spin mb-3" />
                <span className="text-sm text-orange-600 font-medium">
                  {getText(language, 'Analyse du ticket en cours...', 'Анализ изображения...', 'Analizare imagine...', 'Аналіз зображення...')}
                </span>
                <span className="text-xs text-gray-400 mt-1">
                  {getText(language, 'Cela peut prendre quelques secondes', 'Это может занять несколько секунд', 'Poate dura câteva secunde', 'Це може зайняти кілька секунд')}
                </span>
              </div>
            ) : (
              <>
                {/* Hidden file inputs */}
                <input
                  ref={fuelGalleryRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFuelUpload}
                  style={{ position: 'absolute', left: '-9999px' }}
                />
                {/* Camera input for mobile (iOS/Android) */}
                <input
                  ref={fuelCameraRef}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  onChange={handleFuelUpload}
                  style={{ position: 'absolute', left: '-9999px' }}
                />
                
                {/* Camera Error Message */}
                {cameraError && (
                  <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-xl">
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                        <Camera className="w-4 h-4 text-red-600" />
                      </div>
                      <div>
                        <p className="font-medium text-red-700">{cameraError.message}</p>
                        {cameraError.help && (
                          <p className="text-sm text-red-600 mt-1">{cameraError.help}</p>
                        )}
                        <p className="text-sm text-gray-600 mt-2">
                          {getText(language,
                            'Utilisez le bouton "Depuis la galerie" ci-dessous pour sélectionner une photo.',
                            'Используйте кнопку "Из галереи" ниже для выбора фото.',
                            'Folosiți butonul "Din galerie" de mai jos pentru a selecta o fotografie.',
                            'Використовуйте кнопку "З галереї" нижче для вибору фото.'
                          )}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Two buttons: Camera and Gallery */}
                <div className="grid grid-cols-2 gap-4">
                  <button
                    type="button"
                    onClick={handleCameraClick}
                    className={`flex flex-col items-center justify-center p-6 border-2 border-dashed rounded-xl cursor-pointer transition-colors ${
                      cameraError 
                        ? 'border-gray-200 bg-gray-50 opacity-60' 
                        : 'border-orange-300 hover:bg-orange-50 hover:border-orange-400'
                    }`}
                    data-testid="fuel-camera-btn"
                  >
                    <div className={`w-14 h-14 rounded-full flex items-center justify-center mb-3 ${
                      cameraError ? 'bg-gray-100' : 'bg-orange-100'
                    }`}>
                      <Camera className={`w-7 h-7 ${cameraError ? 'text-gray-400' : 'text-orange-600'}`} />
                    </div>
                    <span className={`text-sm font-medium ${cameraError ? 'text-gray-400' : 'text-gray-700'}`}>
                      {getText(language, 'Prendre une photo', 'Сделать фото', 'Fă o poză', 'Зробити фото')}
                    </span>
                  </button>
                  
                  <button
                    type="button"
                    onClick={() => {
                      setCameraError(null);
                      fuelGalleryRef.current?.click();
                    }}
                    className={`flex flex-col items-center justify-center p-6 border-2 border-dashed rounded-xl cursor-pointer transition-colors ${
                      cameraError 
                        ? 'border-green-400 bg-green-50 hover:bg-green-100 hover:border-green-500 ring-2 ring-green-200' 
                        : 'border-gray-300 hover:bg-gray-50 hover:border-gray-400'
                    }`}
                    data-testid="fuel-gallery-btn"
                  >
                    <div className={`w-14 h-14 rounded-full flex items-center justify-center mb-3 ${
                      cameraError ? 'bg-green-100' : 'bg-gray-100'
                    }`}>
                      <Image className={`w-7 h-7 ${cameraError ? 'text-green-600' : 'text-gray-600'}`} />
                    </div>
                    <span className={`text-sm font-medium ${cameraError ? 'text-green-700' : 'text-gray-700'}`}>
                      {getText(language, 'Depuis la galerie', 'Из галереи', 'Din galerie', 'З галереї')}
                    </span>
                    {cameraError && (
                      <span className="text-xs text-green-600 mt-1">
                        {getText(language, '← Recommandé', '← Рекомендуем', '← Recomandat', '← Рекомендовано')}
                      </span>
                    )}
                  </button>
                </div>
              </>
            )}
            
            <Button
              onClick={() => setShowFuelDialog(false)}
              variant="outline"
              className="w-full"
              disabled={uploadingFuel}
            >
              {getText(language, 'Annuler', 'Отмена', 'Anulare', 'Скасувати')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Fuel Photo Viewer Modal */}
      {showFuelPhoto && (
        <div 
          className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
          onClick={() => setShowFuelPhoto(null)}
        >
          <div 
            className="relative bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b">
              <div>
                <h3 className="font-semibold" style={{ color: 'var(--color-primary)' }}>
                  {getText(language, 'Ticket de carburant', 'Чек на топливо', 'Bon carburant', 'Чек на паливо')}
                </h3>
                <p className="text-sm text-gray-500">
                  {new Date(showFuelPhoto.date).toLocaleDateString(locale, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                </p>
              </div>
              <button
                onClick={() => setShowFuelPhoto(null)}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            
            {/* Photo */}
            <div className="p-4 overflow-auto max-h-[60vh]">
              {showFuelPhoto.photo_data ? (
                <img
                  src={showFuelPhoto.photo_data}
                  alt="Ticket carburant"
                  className="w-full h-auto rounded-lg"
                />
              ) : showFuelPhoto.has_photo ? (
                <div className="text-center py-10 text-gray-400 animate-pulse">
                  <Fuel className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                  <p>{getText(language, 'Chargement du ticket...', 'Загрузка квитанции...', 'Se încarcă bonul...', 'Завантаження квитанції...')}</p>
                </div>
              ) : (
                <div className="text-center py-10 text-gray-500">
                  <Fuel className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                  <p>{getText(language, 'Aucun justificatif joint', 'Квитанция не приложена', 'Niciun justificativ atașat', 'Квитанцію не додано')}</p>
                </div>
              )}
            </div>
            
            {/* Footer with info */}
            <div className="p-4 border-t bg-gray-50">
              <div className="flex justify-center gap-6">
                <div className="text-center">
                  <span className="text-2xl font-bold text-orange-600">{showFuelPhoto.euros.toFixed(2)}€</span>
                  <p className="text-xs text-gray-500">{getText(language, 'Montant', 'Сумма', 'Sumă', 'Сума')}</p>
                </div>
                <div className="text-center">
                  <span className="text-2xl font-bold" style={{ color: 'var(--color-primary)' }}>{showFuelPhoto.liters.toFixed(1)}L</span>
                  <p className="text-xs text-gray-500">{getText(language, 'Litres', 'Литров', 'Litri', 'Літрів')}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Webcam Modal */}
      {showWebcam && (
        <div className="fixed inset-0 z-50 bg-black flex flex-col">
          <div className="flex items-center justify-between p-4 bg-black/50">
            <h3 className="text-white font-semibold">
              {getText(language, 'Photographiez le ticket', 'Сфотографируйте чек', 'Fotografiați bonul', 'Сфотографуйте чек')}
            </h3>
            <button onClick={closeWebcam} className="p-2 text-white hover:bg-white/20 rounded-full">
              <X className="w-6 h-6" />
            </button>
          </div>
          
          <div className="flex-1 flex items-center justify-center">
            <video 
              ref={videoRef} 
              autoPlay 
              playsInline 
              className="max-w-full max-h-full"
            />
          </div>
          
          <canvas ref={canvasRef} style={{ display: 'none' }} />
          
          <div className="p-6 bg-black/50 flex justify-center">
            <button
              onClick={capturePhoto}
              className="w-20 h-20 rounded-full bg-white flex items-center justify-center hover:bg-gray-200 transition-colors"
            >
              <div className="w-16 h-16 rounded-full border-4 border-orange-500" />
            </button>
          </div>
        </div>
      )}
      
      {/* Note de Frais Dialog */}
      <Dialog open={showExpenseDialog} onOpenChange={(open) => {
        setShowExpenseDialog(open);
        if (!open) resetExpenseForm();
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center gap-2">
              <Receipt className="w-6 h-6" />
              {getText(language, 'Ajouter une note de frais', 'Добавить расходы', 'Adaugă notă de cheltuieli', 'Додати витрати')}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            {/* Photo Upload */}
            <div>
              <Label className="text-sm font-medium text-gray-700">
                {getText(language, 'Photo du justificatif (facultatif)', 'Фото квитанции (необязательно)', 'Poza justificativului (opțional)', 'Фото квитанції (необов\'язково)')}
              </Label>
              <p className="text-xs text-slate-500 mt-0.5">
                {getText(language, 'Pas de ticket ? Indiquez simplement le montant et ce que vous avez dépensé.', 'Нет чека? Просто укажите сумму и на что потрачено.', 'Fără bon? Indicați doar suma și ce ați cheltuit.', 'Немає чека? Просто вкажіть суму і на що витрачено.')}
              </p>
              
              {/* Hidden file inputs */}
              <input
                ref={expenseGalleryRef}
                type="file"
                accept="image/*"
                onChange={handleExpensePhotoSelect}
                style={{ display: 'none' }}
              />
              <input
                ref={expenseCameraRef}
                type="file"
                accept="image/*"
                capture="environment"
                onChange={handleExpensePhotoSelect}
                style={{ display: 'none' }}
              />
              
              {expensePhoto ? (
                <div className="mt-2 relative">
                  <img 
                    src={expensePhoto} 
                    alt="Preview" 
                    className="w-full h-40 object-cover rounded-lg border"
                  />
                  <button
                    onClick={() => setExpensePhoto(null)}
                    className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <div className="mt-2 grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => expenseCameraRef.current?.click()}
                    className="flex flex-col items-center justify-center p-4 border-2 border-dashed border-purple-300 rounded-xl cursor-pointer hover:bg-purple-50 hover:border-purple-400 transition-colors"
                    data-testid="expense-camera-btn"
                  >
                    <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center mb-2">
                      <Camera className="w-6 h-6 text-purple-600" />
                    </div>
                    <span className="text-sm font-medium text-gray-700">
                      {getText(language, 'Prendre une photo', 'Сделать фото', 'Fă o poză', 'Зробити фото')}
                    </span>
                  </button>
                  
                  <button
                    type="button"
                    onClick={() => expenseGalleryRef.current?.click()}
                    className="flex flex-col items-center justify-center p-4 border-2 border-dashed border-gray-300 rounded-xl cursor-pointer hover:bg-gray-50 hover:border-gray-400 transition-colors"
                    data-testid="expense-gallery-btn"
                  >
                    <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mb-2">
                      <Image className="w-6 h-6 text-gray-600" />
                    </div>
                    <span className="text-sm font-medium text-gray-700">
                      {getText(language, 'Depuis la galerie', 'Из галереи', 'Din galerie', 'З галереї')}
                    </span>
                  </button>
                </div>
              )}
            </div>
            
            {/* Title Input */}
            <div>
              <Label className="text-sm font-medium text-gray-700">
                {getText(language, 'Qu\'avez-vous dépensé ? (commentaire) *', 'На что потрачено? (комментарий) *', 'Ce ați cheltuit? (comentariu) *', 'На що витрачено? (коментар) *')}
              </Label>
              <Input
                value={expenseTitle}
                onChange={(e) => setExpenseTitle(e.target.value)}
                placeholder={getText(language, 'Ex : péage A6, vis et chevilles Leroy Merlin, repas chantier...', 'Напр.: платная дорога, саморезы, обед на объекте...', 'Ex: taxă autostradă, șuruburi, masă pe șantier...', 'Напр.: платна дорога, саморізи, обід на об\'єкті...')}
                className="mt-1"
                required
              />
            </div>
            
            {/* Amount Input */}
            <div>
              <Label className="text-sm font-medium text-gray-700">
                {getText(language, 'Montant (€) *', 'Сумма (€) *', 'Sumă (€) *', 'Сума (€) *')}
              </Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={expenseAmount}
                onChange={(e) => setExpenseAmount(e.target.value)}
                placeholder="0.00"
                className="mt-1 text-lg font-semibold"
                required
              />
            </div>
            
            {/* Buttons */}
            <div className="flex gap-3 pt-2">
              <Button
                onClick={() => {
                  setShowExpenseDialog(false);
                  resetExpenseForm();
                }}
                variant="outline"
                className="flex-1"
                disabled={uploadingExpense}
              >
                {getText(language, 'Annuler', 'Отмена', 'Anulează', 'Скасувати')}
              </Button>
              <Button
                onClick={handleExpenseSubmit}
                className="flex-1 bg-purple-600 hover:bg-purple-700"
                disabled={uploadingExpense || !expenseTitle.trim() || !expenseAmount}
              >
                {uploadingExpense ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {getText(language, 'Envoi...', 'Отправка...', 'Se trimite...', 'Надсилання...')}
                  </>
                ) : (
                  getText(language, 'Ajouter', 'Добавить', 'Adaugă', 'Додати')
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Expense Photo Viewer Modal */}
      {showExpensePhoto && (
        <div 
          className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
          onClick={() => setShowExpensePhoto(null)}
        >
          <div 
            className="relative bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-4 border-b">
              <div>
                <h3 className="font-semibold text-purple-700">{showExpensePhoto.title}</h3>
                <p className="text-sm text-gray-500">
                  {new Date(showExpensePhoto.date).toLocaleDateString(locale, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                </p>
              </div>
              <button
                onClick={() => setShowExpensePhoto(null)}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            
            <div className="p-4 overflow-auto max-h-[70vh]">
              {showExpensePhoto.photo_data ? (
                <img
                  src={showExpensePhoto.photo_data}
                  alt={showExpensePhoto.title}
                  className="w-full h-auto rounded-lg"
                />
              ) : showExpensePhoto.has_photo ? (
                <div className="text-center py-10 text-gray-400 animate-pulse">
                  <Receipt className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                  <p>{getText(language, 'Chargement du justificatif...', 'Загрузка квитанции...', 'Se încarcă justificativul...', 'Завантаження квитанції...')}</p>
                </div>
              ) : (
                <div className="text-center py-10 text-gray-500">
                  <Receipt className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                  <p>{getText(language, 'Aucun justificatif joint', 'Квитанция не приложена', 'Niciun justificativ atașat', 'Квитанцію не додано')}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Chantier du jour, demande au moment du pointage (Pavel 11/09/2026) */}
      <Dialog open={showCheckinProjectDialog} onOpenChange={setShowCheckinProjectDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center gap-2" style={{ color: 'var(--color-primary)' }}>
              <MapPin className="w-6 h-6" />
              {getText(language, 'Sur quel(s) chantier(s) travaillez-vous ?', 'На каком объекте (объектах) вы работаете?', 'La ce șantier(e) lucrați?', 'На якому об\'єкті (об\'єктах) ви працюєте?')}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-xs text-gray-500">
              {getText(language, 'Touchez plusieurs chantiers si vous avez travaillé sur plusieurs aujourd\'hui.', 'Отметьте несколько объектов, если вы работали на нескольких сегодня.', 'Atingeți mai multe șantiere dacă ați lucrat pe mai multe azi.', 'Торкніться кількох об\'єктів, якщо ви працювали на кількох сьогодні.')}
            </p>
            <div className="space-y-2 max-h-[45vh] overflow-y-auto">
              {groupProjects(projects, language).map(group => (
              <div key={group.key} className="space-y-2" data-testid={`checkin-project-group-${group.key}`}>
              <p className="text-xs font-bold uppercase tracking-wide text-slate-500 pt-1">{group.label}</p>
              {group.items.map(project => (
                <button
                  key={project.id}
                  type="button"
                  onClick={() => setCheckinProjectIds(prev => prev.includes(project.id) ? prev.filter(x => x !== project.id) : [...prev, project.id])}
                  className={`w-full text-left px-4 py-3 rounded-xl border text-base transition-colors ${
                    checkinProjectIds.includes(project.id)
                      ? 'bg-emerald-50 border-emerald-400 text-emerald-800 font-semibold'
                      : 'bg-white border-gray-200 hover:bg-gray-50'
                  }`}
                  data-testid={`checkin-project-${project.id}`}
                >
                  <span className="flex items-center justify-between gap-2">
                    <span>{project.name}</span>
                    {checkinProjectIds.includes(project.id) && <CheckCircle2 className="w-5 h-5 flex-shrink-0" />}
                  </span>
                </button>
              ))}
              </div>
              ))}
            </div>

            {projects.length === 0 && (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl">
                <p className="text-sm text-amber-800">
                  {projectsLoading
                    ? getText(language, 'Chargement de la liste des chantiers...', 'Загрузка списка объектов...', 'Se încarcă lista de șantiere...', 'Завантаження списку об\'єктів...')
                    : getText(language, 'La liste des chantiers n\'a pas pu être chargée. Touchez Réessayer.', 'Не удалось загрузить список объектов. Нажмите Повторить.', 'Lista de șantiere nu a putut fi încărcată. Apăsați Reîncercați.', 'Не вдалося завантажити список об\'єктів. Натисніть Повторити.')}
                </p>
                {!projectsLoading && (
                  <button
                    type="button"
                    onClick={fetchProjects}
                    className="mt-2 text-sm font-semibold text-amber-800 bg-amber-100 hover:bg-amber-200 px-3 py-2 rounded-lg"
                    data-testid="retry-checkin-projects-btn"
                  >
                    {getText(language, 'Réessayer', 'Повторить', 'Reîncercați', 'Повторити')}
                  </button>
                )}
              </div>
            )}

            <Button
              onClick={() => doCheckin(checkinProjectIds)}
              className="btn-primary w-full"
              disabled={checkinProjectIds.length === 0 || checkingIn}
              data-testid="confirm-checkin-btn"
            >
              {getText(language, 'Pointer', 'Отметиться', 'Punctează', 'Відмітитися')}
            </Button>

            {/* Chantier obligatoire (ordre Pavel 14/09/2026) : plus de sortie
                "Pointer sans chantier". Le Depot est un chantier comme un autre. */}
            <p className="text-xs text-center text-gray-500 py-1" data-testid="checkin-project-mandatory-hint">
              {getText(language,
                'Le chantier est obligatoire pour pointer.',
                'Объект обязателен для отметки.',
                'Șantierul este obligatoriu pentru pontaj.',
                'Об\'єкт обов\'язковий для відмітки.'
              )}
            </p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Missing Project Yesterday - Blocking Dialog */}
      <Dialog open={showMissingProjectDialog} onOpenChange={(open) => {
        setShowMissingProjectDialog(open);
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-amber-700 flex items-center gap-2">
              <AlertTriangle className="w-6 h-6" />
              {getText(language, 'Chantier manquant pour hier', 'Не указан объект за вчера', 'Șantier lipsă pentru ieri', 'Об\'єкт відсутній за вчора')}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl">
              <p className="text-sm text-amber-800">
                {getText(language,
                  'Vous devez sélectionner le chantier sur lequel vous avez travaillé hier avant de pouvoir pointer aujourd\'hui.',
                  'Вы должны выбрать объект, на котором работали вчера, прежде чем отметиться сегодня.',
                  'Trebuie să selectați șantierul la care ați lucrat ieri înainte de a puncta astăzi.',
                  'Ви повинні обрати об\'єкт, на якому працювали вчора, перш ніж відмітитися сьогодні.'
                )}
              </p>
              {missingProjectRecord && (
                <p className="text-xs text-amber-600 mt-2 font-medium">
                  {getText(language, 'Date', 'Дата', 'Data', 'Дата')}: {new Date(missingProjectRecord.date + 'T12:00:00').toLocaleDateString(locale, { weekday: 'long', day: 'numeric', month: 'long' })}
                </p>
              )}
            </div>
            
            <div>
              <label className="block text-sm font-semibold mb-2" style={{ color: 'var(--color-text)' }}>
                <MapPin className="w-4 h-4 inline mr-2" />
                {getText(language, 'Chantier d\'hier', 'Объект за вчера', 'Șantierul de ieri', 'Об\'єкт за вчора')}
              </label>
              <select 
                value={missingProjectId} 
                onChange={(e) => setMissingProjectId(e.target.value)}
                className="w-full px-3 py-3 border border-gray-300 rounded-xl text-base focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                data-testid="missing-project-selector"
              >
                <option value="">
                  {getText(language, '— Sélectionner un chantier —', '— Выберите объект —', '— Selectați un șantier —', '— Виберіть об\'єкт —')}
                </option>
                {groupProjects(projects, language).map(group => (
                  <optgroup key={group.key} label={group.label}>
                    {group.items.map(project => (
                      <option key={project.id} value={project.id}>
                        {project.name}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
              {projects.length === 0 && (
                <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-xl" data-testid="missing-project-empty">
                  <p className="text-sm text-red-800">
                    {projectsLoading
                      ? getText(language,
                          'Chargement de la liste des chantiers...',
                          'Загрузка списка объектов...',
                          'Se încarcă lista de șantiere...',
                          'Завантаження списку об\'єктів...')
                      : getText(language,
                          'La liste des chantiers n\'a pas pu être chargée (connexion instable). Touchez Réessayer.',
                          'Не удалось загрузить список объектов (нестабильное соединение). Нажмите Повторить.',
                          'Lista de șantiere nu a putut fi încărcată (conexiune instabilă). Apăsați Reîncercați.',
                          'Не вдалося завантажити список об\'єктів (нестабільне з\'єднання). Натисніть Повторити.')}
                  </p>
                  {!projectsLoading && (
                    <button
                      type="button"
                      onClick={fetchProjects}
                      className="mt-2 text-sm font-semibold text-red-700 bg-red-100 hover:bg-red-200 px-3 py-2 rounded-lg"
                      data-testid="retry-projects-btn"
                    >
                      {getText(language, 'Réessayer', 'Повторить', 'Reîncercați', 'Повторити')}
                    </button>
                  )}
                </div>
              )}
            </div>

            <Button
              onClick={saveMissingProject}
              className="btn-primary w-full"
              disabled={!missingProjectId}
              data-testid="save-missing-project-btn"
            >
              {getText(language, 'Enregistrer et continuer', 'Сохранить и продолжить', 'Salvează și continuă', 'Зберегти та продовжити')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      
    </div>
  );
}
